import React, { useState, useEffect } from "react";
import { listPatients as getPatients, getPatient } from "../api/patients.js";

export default function PatientList({ navigate }) {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // full patient detail for modal
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    getPatients().then(setPatients).catch(console.error).finally(() => setLoading(false));
  }, []);

  const openDetail = async (patientCode) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await getPatient(patientCode);
      setDetail(data);
    } catch (err) {
      alert("Failed to load patient: " + err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const filtered = patients.filter((p) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    const hay = [p.id, p.patientCode, p.firstName, p.lastName, p.condition, p.treatment, p.physician]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  if (loading) return <div style={s.loading}>Loading patients...</div>;

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Patients</h1>
        <span style={s.count}>{filtered.length} patients</span>
      </div>

      <div style={s.searchWrap}>
        <svg style={s.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input style={s.search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search patients..." />
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>ID</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>DOB</th>
              <th style={s.th}>Condition</th>
              <th style={s.th}>Treatment</th>
              <th style={s.th}>Physician</th>
              <th style={s.th}>Appts</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const dobLabel = p.dob
                ? new Date(p.dob + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                : "—";
              return (
                <tr key={p.id || p.patientCode} style={s.tr} onClick={() => openDetail(p.patientCode || p.id)}>
                  <td style={s.td}><span style={s.id}>{p.patientCode || p.id}</span></td>
                  <td style={s.td}><span style={s.name}>{p.firstName || ""} {p.lastName || ""}</span></td>
                  <td style={s.td}>{dobLabel}</td>
                  <td style={s.td}>
                    {p.condition ? <span style={s.condBadge}>{p.condition}</span> : <span style={s.muted}>—</span>}
                  </td>
                  <td style={s.td}>{p.treatment || <span style={s.muted}>—</span>}</td>
                  <td style={s.td}>{p.physician || <span style={s.muted}>—</span>}</td>
                  <td style={s.td}>{p.appointmentCount ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Patient Detail Modal */}
      {(detail || detailLoading) && (
        <div style={s.overlay} onClick={() => { setDetail(null); setDetailLoading(false); }}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            {detailLoading ? (
              <div style={s.modalLoading}>Loading patient details...</div>
            ) : detail ? (
              <PatientDetail patient={detail} onClose={() => setDetail(null)} />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelative(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function PatientDetail({ patient, onClose }) {
  const p = patient;
  const confirmedAppts = (p.appointments || []).filter((a) => a.status === "Confirmed");
  const cancelledAppts = (p.appointments || []).filter((a) => a.status === "Cancelled");

  return (
    <>
      {/* Header */}
      <div style={d.header}>
        <div>
          <h2 style={d.name}>{p.firstName} {p.lastName}</h2>
          <div style={d.code}>{p.patientCode}</div>
        </div>
        <button style={d.closeBtn} onClick={onClose}>×</button>
      </div>

      {/* Basic Info */}
      <div style={d.section}>
        <div style={d.sectionTitle}>Patient Information</div>
        <div style={d.grid}>
          <div style={d.field}><span style={d.label}>DOB</span><span style={d.value}>{p.dob || "—"}</span></div>
          <div style={d.field}><span style={d.label}>Phone</span><span style={d.value}>{p.phone || "—"}</span></div>
          <div style={d.field}><span style={d.label}>Email</span><span style={d.value}>{p.email || "—"}</span></div>
          <div style={d.field}><span style={d.label}>Language</span><span style={d.value}>{p.preferredLanguage || "English"}</span></div>
          <div style={d.field}><span style={d.label}>Condition</span><span style={d.value}>{p.condition || "—"}</span></div>
          <div style={d.field}><span style={d.label}>Treatment</span><span style={d.value}>{p.treatment || "—"}</span></div>
          <div style={d.field}><span style={d.label}>Physician</span><span style={d.value}>{p.physician || "—"}</span></div>
          <div style={d.field}><span style={d.label}>Status</span><span style={d.value}>{p.status || "active"}</span></div>
        </div>
        {p.address && (p.address.street || p.address.city) && (
          <div style={d.field}>
            <span style={d.label}>Address</span>
            <span style={d.value}>
              {[p.address.street, p.address.city, p.address.state, p.address.zip].filter(Boolean).join(", ")}
            </span>
          </div>
        )}
      </div>

      {/* Open Intent */}
      {p.openIntent && (
        <div style={d.section}>
          <div style={d.sectionTitle}>
            Open Intent
            <span style={d.openIntentBadge}>Unresolved</span>
            <span style={d.badge}>attempt {p.openIntent.attempt_count}</span>
          </div>
          <div style={d.openIntentCard}>
            <div style={d.openIntentDesc}>{p.openIntent.description || "(no description)"}</div>
            <div style={d.openIntentMeta}>
              {p.openIntent.last_active_agent && (
                <span style={d.agentBadge}>{p.openIntent.last_active_agent}</span>
              )}
              {p.openIntent.last_seen_at && (
                <span style={d.callTime}>
                  last seen {new Date(p.openIntent.last_seen_at).toLocaleString()}
                  {typeof p.openIntent.seconds_since_last_call === "number"
                    ? ` (${formatRelative(p.openIntent.seconds_since_last_call)} ago)` : ""}
                </span>
              )}
              {p.openIntent.call_id && (
                <span style={d.runCode}>{p.openIntent.call_id}</span>
              )}
            </div>
            {p.openIntent.task_snapshot && Object.keys(p.openIntent.task_snapshot).length > 0 && (
              <pre style={d.openIntentSnapshot}>{JSON.stringify(p.openIntent.task_snapshot, null, 2)}</pre>
            )}
          </div>
        </div>
      )}

      {/* Appointments */}
      <div style={d.section}>
        <div style={d.sectionTitle}>
          Appointments
          <span style={d.badge}>{confirmedAppts.length} confirmed</span>
        </div>
        {confirmedAppts.length === 0 && cancelledAppts.length === 0 ? (
          <div style={d.empty}>No appointments</div>
        ) : (
          <div style={d.list}>
            {confirmedAppts.map((a) => (
              <div key={a._id} style={d.apptCard}>
                <div style={d.apptDate}>{a.date} at {a.time}</div>
                <div style={d.apptType}>{a.type}</div>
                <div style={d.apptProvider}>{a.provider}</div>
                <span style={d.apptStatusOk}>Confirmed</span>
              </div>
            ))}
            {cancelledAppts.map((a) => (
              <div key={a._id} style={{ ...d.apptCard, opacity: 0.5 }}>
                <div style={d.apptDate}>{a.date} at {a.time}</div>
                <div style={d.apptType}>{a.type}</div>
                <span style={d.apptStatusCancel}>Cancelled</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Insurance */}
      {(p.insurance || []).length > 0 && (
        <div style={d.section}>
          <div style={d.sectionTitle}>Insurance</div>
          {p.insurance.map((ins) => (
            <div key={ins._id} style={d.insCard}>
              <div style={d.insProvider}>{ins.provider}</div>
              <div style={d.insDetail}>Member ID: {ins.memberId}</div>
              {ins.policyHolderName && <div style={d.insDetail}>Holder: {ins.policyHolderName}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Call Summaries */}
      {(p.callSummaries || []).length > 0 && (
        <div style={d.section}>
          <div style={d.sectionTitle}>
            Recent Calls
            <span style={d.badge}>{p.callSummaries.length}</span>
          </div>
          {p.callSummaries.map((cs) => (
            <div key={cs._id} style={d.callCard}>
              <div style={d.callHeader}>
                <span style={d.callTime}>{new Date(cs.createdAt).toLocaleString()}</span>
                {cs.disposition && (
                  <span style={cs.disposition === "completed" ? d.dispOk : cs.disposition === "disconnected" ? d.dispWarn : d.dispDefault}>
                    {cs.disposition}
                  </span>
                )}
                {cs.agentId && <span style={d.agentBadge}>{cs.agentId}</span>}
              </div>
              <div style={d.callSummary}>{cs.summary}</div>
              {cs.tasksPending?.length > 0 && (
                <div style={d.pending}>Pending: {cs.tasksPending.join(", ")}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Test Runs */}
      {(p.testRuns || []).length > 0 && (
        <div style={d.section}>
          <div style={d.sectionTitle}>
            Test Run History
            <span style={d.badge}>{p.testRuns.length}</span>
          </div>
          <div style={d.list}>
            {p.testRuns.map((tr) => (
              <div key={tr._id} style={d.runCard}>
                <div style={d.runHeader}>
                  <span style={d.runCode}>{tr.runCode}</span>
                  {tr.configMode && (
                    <span style={tr.configMode === "live" ? d.liveBadge : d.draftBadge}>{tr.configMode}</span>
                  )}
                  <span style={tr.status === "completed" ? d.runStatusOk : tr.status === "stopped" ? d.runStatusWarn : d.runStatusErr}>
                    {tr.status}
                  </span>
                  {tr.verification && (
                    <span style={tr.verification.passed ? d.runPass : d.runFail}>
                      {tr.verification.passed ? "Pass" : "Fail"} {tr.verification.score}
                    </span>
                  )}
                </div>
                <div style={d.runDetail}>
                  <span>{tr.testCaseName}</span>
                  <span style={d.runTime}>
                    {tr.startedAt ? new Date(tr.startedAt).toLocaleString() : "—"}
                    {tr.duration != null ? ` (${tr.duration}s)` : ""}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent Memory */}
      {(p.memory || []).length > 0 && (
        <div style={d.section}>
          <div style={d.sectionTitle}>Agent Memory</div>
          {p.memory.map((m) => (
            <div key={m._id} style={d.memCard}>
              <div style={d.memAgent}>{m.agentId}</div>
              <div style={d.memCount}>{(m.entries || []).length} entries</div>
              {(m.entries || []).slice(-2).map((e, i) => (
                <div key={i} style={d.memEntry}>{e.summary?.slice(0, 120)}{e.summary?.length > 120 ? "..." : ""}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999", fontSize: 14 },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "20px 32px 0" },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" },
  count: { fontSize: 13, color: "#999", background: "#f5f5f5", padding: "2px 10px", borderRadius: 10 },
  searchWrap: { position: "relative", margin: "16px 32px 0" },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" },
  search: { width: "100%", padding: "9px 12px 9px 36px", border: "1px solid #e5e5e5", borderRadius: 8, fontSize: 14, outline: "none", color: "#0f0f0f" },
  tableWrap: { margin: "12px 32px 32px", border: "1px solid #e5e5e5", borderRadius: 10, overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 500, color: "#666", borderBottom: "1px solid #e5e5e5", background: "#fafafa", whiteSpace: "nowrap" },
  tr: { borderBottom: "1px solid #f0f0f0", cursor: "pointer" },
  td: { padding: "10px 16px", fontSize: 13, verticalAlign: "middle" },
  id: { fontFamily: "monospace", fontSize: 12, color: "#999" },
  name: { fontWeight: 500, color: "#0f0f0f" },
  condBadge: { padding: "2px 8px", borderRadius: 10, background: "#f0f0f0", color: "#555", fontSize: 12, fontWeight: 500 },
  muted: { color: "#bbb" },
  // Modal
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, paddingTop: 30, overflowY: "auto" },
  modal: { background: "#fff", borderRadius: 12, width: 780, maxHeight: "calc(100vh - 60px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", marginBottom: 30 },
  modalLoading: { padding: 60, textAlign: "center", color: "#999" },
};

const d = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px 12px", borderBottom: "1px solid #f0f0f0" },
  name: { fontSize: 20, fontWeight: 600 },
  code: { fontSize: 12, color: "#999", fontFamily: "monospace", marginTop: 2 },
  closeBtn: { width: 32, height: 32, border: "none", background: "transparent", fontSize: 22, color: "#999", cursor: "pointer" },
  section: { padding: "16px 24px", borderBottom: "1px solid #f5f5f5" },
  sectionTitle: { fontSize: 13, fontWeight: 600, color: "#0f0f0f", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 },
  badge: { fontSize: 11, color: "#666", background: "#f0f0f0", padding: "1px 8px", borderRadius: 10, fontWeight: 400 },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" },
  field: { display: "flex", flexDirection: "column", gap: 2 },
  label: { fontSize: 11, fontWeight: 500, color: "#999", textTransform: "uppercase", letterSpacing: "0.04em" },
  value: { fontSize: 13, color: "#0f0f0f" },
  empty: { fontSize: 13, color: "#bbb", padding: "8px 0" },
  list: { display: "flex", flexDirection: "column", gap: 6 },
  // Appointments
  apptCard: { padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 8, display: "flex", alignItems: "center", gap: 12 },
  apptDate: { fontSize: 13, fontWeight: 600, color: "#0f0f0f", minWidth: 140 },
  apptType: { fontSize: 12, color: "#555", flex: 1 },
  apptProvider: { fontSize: 12, color: "#888" },
  apptStatusOk: { fontSize: 10, fontWeight: 600, color: "#059669", background: "#ecfdf5", padding: "2px 8px", borderRadius: 6 },
  apptStatusCancel: { fontSize: 10, fontWeight: 600, color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: 6 },
  // Insurance
  insCard: { padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 8, marginBottom: 6 },
  insProvider: { fontSize: 13, fontWeight: 600 },
  insDetail: { fontSize: 12, color: "#666" },
  // Call Summaries
  callCard: { padding: "10px 12px", border: "1px solid #e5e5e5", borderRadius: 8, marginBottom: 6 },
  callHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  callTime: { fontSize: 11, color: "#999" },
  dispOk: { fontSize: 10, fontWeight: 600, color: "#059669", background: "#ecfdf5", padding: "1px 6px", borderRadius: 4 },
  dispWarn: { fontSize: 10, fontWeight: 600, color: "#f59e0b", background: "#fef3c7", padding: "1px 6px", borderRadius: 4 },
  dispDefault: { fontSize: 10, fontWeight: 600, color: "#666", background: "#f0f0f0", padding: "1px 6px", borderRadius: 4 },
  agentBadge: { fontSize: 10, color: "#1d4ed8", background: "#eff6ff", padding: "1px 6px", borderRadius: 4 },
  callSummary: { fontSize: 13, color: "#333", lineHeight: 1.5 },
  pending: { fontSize: 12, color: "#f59e0b", marginTop: 4, fontWeight: 500 },
  // Test Runs
  runCard: { padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 8, marginBottom: 4 },
  runHeader: { display: "flex", alignItems: "center", gap: 6, marginBottom: 3 },
  runCode: { fontSize: 11, fontFamily: "monospace", color: "#999" },
  draftBadge: { fontSize: 9, fontWeight: 600, color: "#1d4ed8", background: "#eff6ff", padding: "1px 5px", borderRadius: 3, textTransform: "uppercase" },
  liveBadge: { fontSize: 9, fontWeight: 600, color: "#dc2626", background: "#fef2f2", padding: "1px 5px", borderRadius: 3, textTransform: "uppercase" },
  runStatusOk: { fontSize: 10, color: "#059669", fontWeight: 500 },
  runStatusWarn: { fontSize: 10, color: "#f59e0b", fontWeight: 500 },
  runStatusErr: { fontSize: 10, color: "#dc2626", fontWeight: 500 },
  runPass: { fontSize: 10, fontWeight: 600, color: "#059669", background: "#ecfdf5", padding: "1px 5px", borderRadius: 3 },
  runFail: { fontSize: 10, fontWeight: 600, color: "#dc2626", background: "#fef2f2", padding: "1px 5px", borderRadius: 3 },
  runDetail: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#555" },
  runTime: { fontSize: 11, color: "#999" },
  // Open Intent
  openIntentBadge: { fontSize: 10, fontWeight: 600, color: "#b45309", background: "#fef3c7", padding: "2px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.04em" },
  openIntentCard: { padding: "12px 14px", border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8 },
  openIntentDesc: { fontSize: 13, color: "#0f0f0f", fontWeight: 500, lineHeight: 1.5 },
  openIntentMeta: { display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" },
  openIntentSnapshot: { marginTop: 10, padding: 8, background: "#fff", border: "1px solid #fde68a", borderRadius: 6, fontSize: 11, fontFamily: "monospace", color: "#555", overflow: "auto", maxHeight: 160 },
  // Memory
  memCard: { padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 8, marginBottom: 6 },
  memAgent: { fontSize: 12, fontWeight: 600, color: "#1d4ed8" },
  memCount: { fontSize: 11, color: "#999" },
  memEntry: { fontSize: 12, color: "#555", lineHeight: 1.4, marginTop: 4 },
};
